import { html, nothing } from "lit";
import {
  renderInspectorWidthPicker,
  type FleetInspectorWidthPickerProps,
} from "./fleet-inspector-width-picker.ts";
import type {
  FleetInspectorTabId,
  FleetWorkspaceDensity,
  FleetWorkspaceLayoutState,
  FleetWorkspacePanelId,
  FleetWorkspacePreset,
} from "./fleet-workspace-presets.ts";
import { renderFleetWorkspaceResizer } from "./fleet-workspace-resizer.ts";

/** @deprecated Legacy drawer grouping — kept for settings migration only. */
export type FleetUnifiedDrawerTab = "files" | "inspector";

/** Floating panel state for detached panels */
export interface FloatingPanelState {
  id: FleetWorkspacePanelId;
  x: number;
  y: number;
  width: number;
  height: number;
  minimized: boolean;
  label: string;
}

/** Taskbar item for minimized panels */
export interface TaskbarItem {
  id: FleetWorkspacePanelId;
  label: string;
  minimized: boolean;
}

export type FleetUnifiedLayoutProps = {
  layout: FleetWorkspaceLayoutState;
  density: FleetWorkspaceDensity;
  sideTab: FleetInspectorTabId;
  sidePanelOpen: boolean;
  panels: {
    left: unknown;
    right: unknown;
  };
  floatingPanels?: FloatingPanelState[];
  /** JAISIU-3081: persisted inspector width in pixels (0 = use preset default). */
  inspectorWidth?: number;
  /** JAISIU-3081: persist the new inspector width after a drag. */
  onInspectorResize?: (nextWidthPx: number) => void;
  /** JAISIU-3082 (operator tweak): discrete Inspector width picker state. */
  inspectorWidthPicker?: FleetInspectorWidthPickerProps;
  onSideTabChange: (tab: FleetInspectorTabId) => void;
  onToggleSidePanel: () => void;
  onColumnResize: (ratio: number) => void;
  onDetachPanel?: (panelId: FleetWorkspacePanelId) => void;
  onFloatPanel?: (
    panelId: FleetWorkspacePanelId,
    x: number,
    y: number,
    width: number,
    height: number,
  ) => void;
  onCloseFloatingPanel?: (panelId: FleetWorkspacePanelId) => void;
  onMinimizeFloatingPanel?: (panelId: FleetWorkspacePanelId) => void;
  onRestoreFloatingPanel?: (panelId: FleetWorkspacePanelId) => void;
  onMoveFloatingPanel?: (panelId: FleetWorkspacePanelId, x: number, y: number) => void;
};

function panelVisible(layout: FleetWorkspaceLayoutState, id: FleetWorkspacePanelId): boolean {
  return layout.visiblePanels[id] !== false;
}

function panelLabel(id: FleetWorkspacePanelId): string {
  const labels: Record<string, string> = {
    transcript: "Chat",
    inspector: "Inspector",
    directory: "Directory",
    controls: "Controls",
    analytics: "Analytics",
  };
  return labels[id] || id;
}

function sideColumnRatio(layout: FleetWorkspaceLayoutState): number {
  const [a, , c] = layout.columns;
  const left = a > 0 ? a : 0.58;
  const right = c > 0 ? c : 0.42;
  const sum = left + right;
  return sum > 0 ? right / sum : 0.42;
}

function styleVars(
  layout: FleetWorkspaceLayoutState,
  inspectorWidthIndex: number,
): Record<string, string> {
  // JAISIU-3082: inspectorWidthIndex is a 0..2 index into
  // INSPECTOR_WIDTH_PRESETS (Compact=0, Default=1, Wide=2). Convert to
  // the pixel value the right column should adopt.
  const presets = [280, 380, 520];
  const clampedIndex = Math.max(0, Math.min(2, inspectorWidthIndex | 0));
  const inspectorWidthPx = presets[clampedIndex];

  const containerWidthGuess = 1200;
  const ratio = Math.min(0.85, Math.max(0.15, inspectorWidthPx / containerWidthGuess));
  return {
    "--fleet-ws-col-main": String(1 - ratio),
    "--fleet-ws-col-drawer": String(ratio),
    "--fleet-inspector-width": `${inspectorWidthPx}px`,
  };
}

function renderLeftColumn(props: FleetUnifiedLayoutProps) {
  return html`
    <section class="fleet-unified__left" aria-label="Chat transcript" data-panel="transcript">
      <div class="fleet-unified__left-body">${props.panels.left ?? nothing}</div>
    </section>
  `;
}

function renderRightColumn(props: FleetUnifiedLayoutProps) {
  // The collapsed-side-rail (with quick-tabs) was deleted: inspector
  // visibility is now owned solely by the topbar inspector toggle
  // (`.topbar-inspector-toggle`). The right column ALWAYS renders
  // when the workspace has the inspector enabled; the .hidden class
  // (set from props.sidePanelOpen) toggles visibility via CSS. This
  // keeps the DOM stable across renders so Lit's diff never gets
  // confused about conditional subtree state.
  return html`
    <aside
      class="fleet-unified__right"
      aria-label="Fleet tools and inspector"
      data-panel="inspector"
      data-side-panel-open=${props.sidePanelOpen ? "true" : "false"}
      ?hidden=${!props.sidePanelOpen}
    >
      <header
        class="fleet-unified__right-header ${props.density === "compact" ? "fleet-unified__right-header--compact" : ""}"
      >
        <div class="fleet-unified__right-title-group">
          <h3 class="fleet-unified__right-title">INSPECTOR</h3>
          ${
            props.inspectorWidthPicker
              ? renderInspectorWidthPicker({
                  selectedIndex: props.inspectorWidthPicker.selectedIndex,
                  onSelect: props.inspectorWidthPicker.onSelect,
                  // JAISIU-types-sweep: dropped the `compact:
                  // …picker.compact ?? true` pass-through. No producer
                  // ever set `compact` (the only builder,
                  // `chat-thread-render.ts`, passes `selectedIndex`,
                  // `onSelect`, `asMenu`) and
                  // `renderInspectorWidthPicker` never read it, so the
                  // literal only existed to fail the excess-property
                  // check.
                  asMenu: props.inspectorWidthPicker.asMenu,
                })
              : nothing
          }
        </div>
        <div class="fleet-unified__header-actions">
          ${
            props.onDetachPanel
              ? html`
                  <button
                    type="button"
                    class="fleet-unified__detach-btn"
                    title="Detach panel to float"
                    aria-label="Detach panel to float"
                    @click=${() => props.onDetachPanel?.("inspector")}
                  >
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <rect x="3" y="3" width="18" height="18" rx="2" />
                      <path d="M9 3v18M15 3v18" />
                    </svg>
                  </button>
                `
              : nothing
          }
          <button
            type="button"
            class="fleet-unified__side-collapse"
            title="Hide side panel"
            aria-label="Hide side panel"
            @click=${props.onToggleSidePanel}
          >
            ›
          </button>
        </div>
      </header>
      <div class="fleet-unified__right-body">${props.panels.right ?? nothing}</div>
    </aside>
  `;
}

/** Render a floating panel */
function renderFloatingPanel(
  panel: FloatingPanelState,
  content: unknown,
  props: FleetUnifiedLayoutProps,
): ReturnType<typeof html> | typeof nothing {
  const handleMouseDown = (e: MouseEvent) => {
    if ((e.target as HTMLElement).closest(".fleet-floating-panel__actions")) {
      return;
    }
    e.preventDefault();
    const startX = e.clientX;
    const startY = e.clientY;
    const startPanelX = panel.x;
    const startPanelY = panel.y;

    const handleMouseMove = (moveEvent: MouseEvent) => {
      const dx = moveEvent.clientX - startX;
      const dy = moveEvent.clientY - startY;
      props.onMoveFloatingPanel?.(panel.id, startPanelX + dx, startPanelY + dy);
    };

    const handleMouseUp = () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  if (panel.minimized) {
    return nothing;
  }

  return html`
    <div
      class="fleet-floating-panel"
      style="left:${panel.x}px;top:${panel.y}px;width:${panel.width}px;height:${panel.height}px;"
      data-panel-id=${panel.id}
    >
      <header class="fleet-floating-panel__header" @mousedown=${handleMouseDown}>
        <span class="fleet-floating-panel__title">${panel.label}</span>
        <div class="fleet-floating-panel__actions">
          <button
            type="button"
            class="fleet-floating-panel__minimize"
            title="Minimize to taskbar"
            aria-label="Minimize to taskbar"
            @click=${() => props.onMinimizeFloatingPanel?.(panel.id)}
          >
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
          </button>
          <button
            type="button"
            class="fleet-floating-panel__close"
            title="Close panel"
            aria-label="Close panel"
            @click=${() => props.onCloseFloatingPanel?.(panel.id)}
          >
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
      </header>
      <div class="fleet-floating-panel__content">${content}</div>
    </div>
  `;
}

/** Render taskbar for minimized floating panels */
function renderFloatingTaskbar(
  panels: FloatingPanelState[],
  props: FleetUnifiedLayoutProps,
): ReturnType<typeof html> | typeof nothing {
  if (panels.length === 0) {
    return nothing;
  }

  return html`
    <div class="fleet-floating-taskbar" role="toolbar" aria-label="Floating panels taskbar">
      ${panels.map(
        (panel) => html`
          <button
            type="button"
            class="fleet-floating-taskbar__item ${panel.minimized ? "fleet-floating-taskbar__item--minimized" : ""}"
            title=${panel.label}
            aria-label="Restore ${panel.label}"
            @click=${() => props.onRestoreFloatingPanel?.(panel.id)}
          >
            <span class="fleet-floating-taskbar__icon">
              ${
                panel.id === "inspector"
                  ? html`
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                      >
                        <rect x="3" y="3" width="18" height="18" rx="2" />
                        <path d="M9 3v18M15 3v18" />
                      </svg>
                    `
                  : html`
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                      >
                        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                      </svg>
                    `
              }
            </span>
            <span class="fleet-floating-taskbar__label">${panel.label}</span>
          </button>
        `,
      )}
    </div>
  `;
}

function renderResizeHandle(props: FleetUnifiedLayoutProps, showRight: boolean) {
  if (!props.sidePanelOpen || !showRight) {
    return nothing;
  }
  // JAISIU-3082: the Inspector width is now controlled by a 3-option
  // segmented picker in the topbar (renderInspectorWidthPicker). No
  // in-layout draggable handle — the column width is set via the
  // --fleet-inspector-width CSS variable derived from the chosen
  // index. Render nothing here.
  void props;
  return nothing;
}

export function renderFleetUnifiedLayout(props: FleetUnifiedLayoutProps) {
  const showLeft = panelVisible(props.layout, "transcript");
  const showRight = panelVisible(props.layout, "inspector");
  const floatingPanels = props.floatingPanels ?? [];
  const floatingRight = floatingPanels.find((p) => p.id === "inspector");

  return html`
    <div
      class="fleet-unified ${props.density === "compact" ? "fleet-unified--compact" : "fleet-unified--comfortable"} ${showLeft ? "" : "fleet-unified--left-hidden"} ${showRight && !floatingRight ? "" : "fleet-unified--right-hidden"}"
      style=${Object.entries(styleVars(props.layout, props.inspectorWidth ?? 0))
        .map(([k, v]) => `${k}:${v}`)
        .join(";")}
      aria-label="Chat: chat left, tools right"
    >
      ${showLeft ? renderLeftColumn(props) : nothing}
      ${renderResizeHandle(props, showRight && !floatingRight)}
      ${showRight && !floatingRight ? renderRightColumn(props) : nothing}

      <!-- Floating panels overlay -->
      <div class="fleet-floating-panels-overlay" aria-live="polite">
        ${floatingRight ? renderFloatingPanel(floatingRight, props.panels.right, props) : nothing}
      </div>

      <!-- Taskbar for minimized panels -->
      ${floatingPanels.length > 0 ? renderFloatingTaskbar(floatingPanels, props) : nothing}
    </div>
  `;
}

export const FLEET_UNIFIED_PANEL_IDS: FleetWorkspacePanelId[] = ["transcript", "inspector"];

/** @deprecated Use fleetInspectorTab directly in unified layout. */
export function drawerTabForInspectorTab(tab: FleetInspectorTabId): FleetUnifiedDrawerTab {
  if (tab === "files") {
    return "files";
  }
  return "inspector";
}

/** @deprecated Use fleetInspectorTab directly in unified layout. */
export function inspectorTabForDrawerTab(tab: FleetUnifiedDrawerTab): FleetInspectorTabId {
  if (tab === "files") {
    return "files";
  }
  return "agents";
}

export function defaultUnifiedDrawerOpenForPreset(preset: FleetWorkspacePreset): boolean {
  return preset === "unified";
}
